const ECHO_AUDIO_CONFIG_URL = "/api/echo/config";
const ECHO_AUDIO_CHAPTER_URL = "/api/echo/chapter";
const ECHO_AUDIO_EXPORT_CHAPTER_URL = "/api/echo/export-chapter";

function hashString(value) {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
}

export function createChapterAudioCacheKey({
  chapterTitle,
  chapterText,
  selectedEnglishVoiceURI,
  selectedFrenchVoiceURI,
  selectedChineseVoiceURI
}) {
  return hashString(
    [
      chapterTitle || "",
      chapterText || "",
      selectedEnglishVoiceURI || "",
      selectedFrenchVoiceURI || "",
      selectedChineseVoiceURI || ""
    ].join("::")
  );
}

export async function fetchEchoAudioConfig() {
  const response = await fetch(ECHO_AUDIO_CONFIG_URL, {
    method: "GET",
    cache: "no-store"
  });

  if (!response.ok) {
    throw new Error("Could not load PageTone audio configuration.");
  }

  return response.json();
}

async function readErrorMessage(response, fallbackMessage) {
  try {
    const rawBody = await response.text();

    if (!rawBody) {
      return fallbackMessage;
    }

    try {
      const payload = JSON.parse(rawBody);
      if (payload?.error) {
        return payload.error;
      }
    } catch (error) {
      // Fall back to the raw text body below.
    }

    return rawBody;
  } catch (error) {
    return fallbackMessage;
  }
}

function decodeDurationsHeader(value) {
  if (!value) {
    return [];
  }

  try {
    return JSON.parse(window.atob(value));
  } catch (error) {
    return [];
  }
}

export async function requestChapterAudio({
  segments,
  selectedEnglishVoiceURI,
  selectedFrenchVoiceURI,
  selectedChineseVoiceURI,
  personalApiKey,
  preferStreamResponse = false
}) {
  const headers = {
    "Content-Type": "application/json"
  };

  if (personalApiKey && personalApiKey.trim()) {
    headers["x-openai-api-key"] = personalApiKey.trim();
  }

  if (preferStreamResponse) {
    headers["x-echo-response-mode"] = "metadata";
  }

  const response = await fetch(ECHO_AUDIO_CHAPTER_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({
      segments,
      selectedEnglishVoiceURI,
      selectedFrenchVoiceURI,
      selectedChineseVoiceURI
    })
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, "Chapter audio generation failed."));
  }

  if (preferStreamResponse) {
    const payload = await response.json();

    return {
      mimeType: payload?.mimeType || "audio/wav",
      durations: Array.isArray(payload?.durations) ? payload.durations : [],
      cacheStatus: payload?.cacheStatus || response.headers.get("x-echo-cache-status") || "",
      cacheStore: payload?.cacheStore || response.headers.get("x-echo-cache-store") || "",
      cacheKey: payload?.cacheKey || response.headers.get("x-echo-cache-key") || "",
      cacheDedupe:
        payload?.deduped
          ? "in-flight"
          : response.headers.get("x-echo-cache-dedupe") || "",
      streamUrl: payload?.streamUrl || "",
      audioBuffer: null
    };
  }

  return {
    mimeType: response.headers.get("content-type") || "audio/wav",
    durations: decodeDurationsHeader(response.headers.get("x-echo-segment-durations")),
    cacheStatus: response.headers.get("x-echo-cache-status") || "",
    cacheStore: response.headers.get("x-echo-cache-store") || "",
    cacheKey: response.headers.get("x-echo-cache-key") || "",
    cacheDedupe: response.headers.get("x-echo-cache-dedupe") || "",
    streamUrl: "",
    audioBuffer: await response.arrayBuffer()
  };
}

export async function exportChapterAudio({
  text,
  language,
  chapterTitle
}) {
  const response = await fetch(ECHO_AUDIO_EXPORT_CHAPTER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      text,
      language,
      chapterTitle
    })
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, "Chapter export failed."));
  }

  return {
    mimeType: response.headers.get("content-type") || "audio/mpeg",
    fileName:
      /filename="?([^"]+)"?/i.exec(response.headers.get("content-disposition") || "")?.[1] || "",
    audioBuffer: await response.arrayBuffer()
  };
}
